import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { supabase } from '../supabaseClient';
import { authAPI } from '../lib/api';
import { useAuthStore, getDefaultRouteForUser, getUserAccessState } from '../lib/auth';
import { getErrorMessage } from '../lib/errorMessage';

const ACTIVATION_FLOW = {
  UNKNOWN: 'unknown',
  RECOVERY: 'recovery',
  SUPABASE_INVITE: 'supabase_invite',
  LEGACY_TOKEN: 'legacy_token',
};

const toInviteRecord = (raw) => {
  if (!raw) return null;
  if (Array.isArray(raw)) return raw[0] || null;
  return raw;
};

const isInviteInvalid = (invite) => {
  if (!invite) return 'Invite not found.';
  const status = String(invite.status || invite.invite_status || '').toLowerCase();

  if (!status) return 'Invite status is missing.';
  if (status === 'accepted') return 'This invite has already been accepted.';
  if (status === 'revoked') return 'This invite has been revoked.';
  if (status === 'expired') return 'This invite has expired.';
  if (status !== 'pending') return `Invite is ${status} and cannot be activated.`;

  const expiresAt = invite.expires_at || invite.invite_expires_at;
  if (expiresAt) {
    const expiresTs = new Date(expiresAt).getTime();
    if (Number.isFinite(expiresTs) && Date.now() > expiresTs) {
      return 'This invite has expired.';
    }
  }

  return null;
};

export default function ActivateAccountPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const hashParams = useMemo(() => {
    if (typeof window === 'undefined') return new URLSearchParams();
    const rawHash = window.location.hash || '';
    if (!rawHash.startsWith('#') || rawHash.length <= 1) return new URLSearchParams();
    return new URLSearchParams(rawHash.slice(1));
  }, []);

  const queryType = (searchParams.get('type') || '').toLowerCase();
  const hashType = (hashParams.get('type') || '').toLowerCase();
  const callbackType = queryType || hashType;
  const mode = (searchParams.get('mode') || hashParams.get('mode') || '').toLowerCase();
  const callbackCode = searchParams.get('code') || hashParams.get('code');
  const hasCallbackHashTokens = Boolean(hashParams.get('access_token') || hashParams.get('refresh_token'));
  const hasExplicitInviteType = callbackType === 'invite' || callbackType === 'signup';

  const recoveryMode = mode === 'recovery' || callbackType === 'recovery';
  const supabaseInviteMode =
    !recoveryMode &&
    !token &&
    (
      hasExplicitInviteType ||
      hasCallbackHashTokens
    );

  const { user, login, refreshUser, logout } = useAuthStore();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [activationFlow, setActivationFlow] = useState(ACTIVATION_FLOW.UNKNOWN);

  const accessState = useMemo(() => getUserAccessState(user), [user]);

  useEffect(() => {
    const loadActivationContext = async () => {
      setLoading(true);
      setError('');

      try {
        // Flow 1: Supabase password recovery.
        if (recoveryMode) {
          setActivationFlow(ACTIVATION_FLOW.RECOVERY);

          if (callbackCode) {
            const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(callbackCode);
            if (exchangeError) throw exchangeError;
          }

          const { data: userData, error: userError } = await supabase.auth.getUser();
          if (userError) throw userError;

          const recoveryEmail = userData?.user?.email;
          if (!recoveryEmail) {
            throw new Error('Unable to resolve the account for this password reset link.');
          }

          setInvite({
            email: recoveryEmail,
            first_name: userData?.user?.user_metadata?.first_name || 'there',
          });
          return;
        }

        // Flow 2: Supabase invite-email first-time activation.
        // Do not classify as invite from code alone; require explicit invite/signup callback type
        // or a resolved authenticated user session from the callback.
        if (!token && (supabaseInviteMode || callbackCode)) {

          if (callbackCode) {
            const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(callbackCode);
            if (exchangeError) throw exchangeError;
          }

          const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
          if (sessionError) throw sessionError;

          let inviteUser = sessionData?.session?.user || null;
          if (!inviteUser) {
            const { data: userData, error: userError } = await supabase.auth.getUser();
            if (userError) throw userError;
            inviteUser = userData?.user || null;
          }

          const inviteEmail = inviteUser?.email;
          const hasResolvedInviteUser = Boolean(inviteEmail);
          const shouldTreatAsInvite = hasExplicitInviteType || supabaseInviteMode || hasResolvedInviteUser;

          if (shouldTreatAsInvite && hasResolvedInviteUser) {
            setActivationFlow(ACTIVATION_FLOW.SUPABASE_INVITE);
            setInvite({
              email: inviteEmail,
              first_name: inviteUser?.user_metadata?.first_name || 'there',
            });
            return;
          }

          if (hasExplicitInviteType || supabaseInviteMode) {
            throw new Error('Unable to resolve the invited account from this activation link. Please use the latest invite email.');
          }
        }

        // Flow 3: Legacy RTB token activation.
        setActivationFlow(ACTIVATION_FLOW.LEGACY_TOKEN);
        if (!token) {
          setError('Activation token is missing. Please open the invite link from your email.');
          return;
        }

        const { data, error: rpcError } = await supabase.rpc('get_activation_invite', { p_token: token });
        if (rpcError) throw rpcError;

        const inviteRecord = toInviteRecord(data);
        const invalidReason = isInviteInvalid(inviteRecord);
        if (invalidReason) {
          setInvite(inviteRecord);
          setError(invalidReason);
          return;
        }

        setInvite(inviteRecord);
      } catch (err) {
        setError(getErrorMessage(err, 'Invalid or expired activation link.'));
      } finally {
        setLoading(false);
      }
    };

    loadActivationContext();
  }, [callbackCode, hasExplicitInviteType, recoveryMode, supabaseInviteMode, token]);

  const validateForm = () => {
    const nextErrors = {};

    if (!password) {
      nextErrors.password = 'Password is required.';
    } else if (password.length < 8) {
      nextErrors.password = 'Password must be at least 8 characters.';
    }

    if (!confirmPassword) {
      nextErrors.confirmPassword = 'Please confirm your password.';
    } else if (confirmPassword !== password) {
      nextErrors.confirmPassword = 'Passwords do not match.';
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const ensureAuthIdentity = async (email, nextPassword) => {
    const signup = await supabase.auth.signUp({
      email,
      password: nextPassword,
    });

    if (signup.error) {
      const msg = String(signup.error.message || '').toLowerCase();
      const userExists = msg.includes('already') || msg.includes('registered') || msg.includes('exists');
      if (!userExists) throw signup.error;
    }

    const signin = await supabase.auth.signInWithPassword({
      email,
      password: nextPassword,
    });

    if (signin.error) throw signin.error;

    const current = await supabase.auth.getUser();
    if (current.error) throw current.error;

    const authUserId = current.data?.user?.id;
    if (!authUserId) throw new Error('Could not resolve authenticated user ID after password setup.');

    return authUserId;
  };

  const handleActivate = async (event) => {
    event.preventDefault();

    if (!invite?.email) {
      setError('Invite is missing an email address.');
      return;
    }

    if (!validateForm()) return;

    setSubmitting(true);
    setError('');

    try {
      if (activationFlow === ACTIVATION_FLOW.RECOVERY) {
        const { error: updateError } = await supabase.auth.updateUser({ password });
        if (updateError) throw updateError;
      } else if (activationFlow === ACTIVATION_FLOW.SUPABASE_INVITE) {
        const { error: updateError } = await supabase.auth.updateUser({ password });
        if (updateError) throw updateError;

        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;

        const supabaseAccessToken = sessionData?.session?.access_token;
        if (!supabaseAccessToken) {
          throw new Error('Could not resolve Supabase session while finalizing invite activation.');
        }

        await authAPI.completeSupabaseInvite(supabaseAccessToken);
      } else {
        if (!token) {
          throw new Error('Activation token is missing for legacy activation flow.');
        }

        await ensureAuthIdentity(invite.email, password);

        const { error: completeErr } = await supabase.rpc('complete_invite_activation', {
          p_token: token,
        });
        if (completeErr) throw completeErr;
      }

      const loginResult = await login(invite.email, password);
      if (!loginResult?.success || !loginResult.user) {
        throw new Error(loginResult?.error || 'Account activated but sign-in failed. Please sign in manually.');
      }

      await refreshUser();

      const nextState = getUserAccessState(loginResult.user);
      if (nextState === 'disabled') {
        navigate('/account-disabled', { replace: true });
        return;
      }
      if (nextState === 'pending_activation') {
        throw new Error('Activation completed but account is still pending. Please contact support.');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate(getDefaultRouteForUser(loginResult.user), { replace: true });
      }, 1200);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to activate account.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#121212] flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-[#FF6600] animate-spin mx-auto mb-3" />
          <p className="text-white">Loading activation invite...</p>
        </div>
      </div>
    );
  }

  if (
    !token &&
    activationFlow === ACTIVATION_FLOW.UNKNOWN &&
    accessState === 'pending_activation'
  ) {
    return (
      <div className="min-h-screen bg-[#121212] flex items-center justify-center p-4">
        <div className="max-w-lg w-full card-rtb space-y-4">
          <h1 className="font-heading text-2xl text-white uppercase">Activation Required</h1>
          <p className="text-[#A1A1AA] text-sm">
            Your account is pending activation. Please use the invite activation link from your email.
          </p>
          <Button className="btn-rtb-secondary" onClick={() => logout()}>
            Sign Out
          </Button>
        </div>
      </div>
    );
  }

  if (
    error &&
    (
      activationFlow === ACTIVATION_FLOW.UNKNOWN ||
      (activationFlow === ACTIVATION_FLOW.LEGACY_TOKEN && (!invite || isInviteInvalid(invite)))
    )
  ) {
    return (
      <div className="min-h-screen bg-[#121212] flex items-center justify-center p-4">
        <div className="max-w-lg w-full card-rtb space-y-3 text-center">
          <AlertCircle className="w-10 h-10 text-[#FF3B30] mx-auto" />
          <h1 className="font-heading text-2xl text-white uppercase">Activation Unavailable</h1>
          <p className="text-[#A1A1AA] text-sm">{error}</p>
          <Button className="btn-rtb-secondary" onClick={() => navigate('/login')}>
            Go To Login
          </Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#121212] flex items-center justify-center p-4">
        <div className="max-w-lg w-full card-rtb space-y-3 text-center">
          <CheckCircle2 className="w-10 h-10 text-[#00E676] mx-auto" />
          <h1 className="font-heading text-2xl text-white uppercase">Account Activated</h1>
          <p className="text-[#A1A1AA] text-sm">Your account is ready. Redirecting now...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#121212] flex items-center justify-center p-4">
      <div className="w-full max-w-lg card-rtb">
        <h1 className="font-heading text-3xl text-white uppercase">Raise The Bar</h1>
        <p className="text-[#E4E4E7] mt-3">Hi {invite?.first_name || 'there'}</p>
        <p className="text-[#A1A1AA] mt-2 text-sm">
          {activationFlow === ACTIVATION_FLOW.RECOVERY
            ? 'Please choose a new password for your RTB account.'
            : activationFlow === ACTIVATION_FLOW.SUPABASE_INVITE
            ? 'Please set your password to activate your invited RTB account.'
            : 'Please create a password for your new account to activate access to the RTB platform.'}
        </p>

        {error ? (
          <div className="mt-4 bg-[#FF3B30]/10 border border-[#FF3B30]/40 rounded-md px-3 py-2 text-[#FFB4AF] text-sm">
            {error}
          </div>
        ) : null}

        <form className="space-y-4 mt-6" onSubmit={handleActivate}>
          <div>
            <Label className="text-[#A1A1AA]">Email</Label>
            <Input value={invite?.email || ''} disabled className="input-rtb mt-1 opacity-70" type="email" />
          </div>

          <div>
            <Label className="text-[#A1A1AA]">Password</Label>
            <div className="relative mt-1">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`input-rtb pr-10 ${formErrors.password ? 'border-[#FF3B30]' : ''}`}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {formErrors.password ? <p className="text-[#FFB4AF] text-xs mt-1">{formErrors.password}</p> : null}
          </div>

          <div>
            <Label className="text-[#A1A1AA]">Confirm Password</Label>
            <div className="relative mt-1">
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`input-rtb pr-10 ${formErrors.confirmPassword ? 'border-[#FF3B30]' : ''}`}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {formErrors.confirmPassword ? <p className="text-[#FFB4AF] text-xs mt-1">{formErrors.confirmPassword}</p> : null}
          </div>

          <Button type="submit" className="btn-rtb-primary w-full" disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Activating...
              </>
            ) : (
              'Activate Account'
            )}
          </Button>
        </form>

        <p className="text-[#A1A1AA] text-xs mt-6 leading-relaxed">
          Please note: we are currently still in the trial phase, so if you experience any bugs, issues or reporting problems, please report them to{' '}
          <a href="mailto:Jordan@raisethebaruk.com" className="text-[#FF6600] hover:text-[#FF8533]">
            Jordan@raisethebaruk.com
          </a>{' '}
          or your line manager.
        </p>
      </div>
    </div>
  );
}
